import React, { useState, useEffect, useRef, useCallback } from 'react';
import { submitSnakeScore } from '../../service/SnakeService';

const CELL = 18;
const COLS = () => Math.floor(window.innerWidth / CELL);
const ROWS = () => Math.floor(window.innerHeight / CELL);
const FOOD_COUNT = 8;
const INITIAL_SPEED = 120;
const MIN_SPEED = 50;

const SnakeMode = ({ audioContext, isSoundEnabled, onExit }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const animRef = useRef(null);
  const lastMoveRef = useRef(0);
  const pendingDirRef = useRef({ x: 0, y: -1 });
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [isDead, setIsDead] = useState(false);
  const [showHint, setShowHint] = useState(true);
  const [recordMsg, setRecordMsg] = useState(null); // null | 'personal' | 'server'
  const [bestScore, setBestScore] = useState(null); // personal best from server

  const playEat = useCallback(() => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    const freqs = [523, 659, 784];
    freqs.forEach((f, i) => {
      const osc = audioContext.createOscillator(); const g = audioContext.createGain();
      osc.connect(g); g.connect(audioContext.destination);
      osc.type = 'sine'; osc.frequency.value = f;
      const t = now + i * 0.06;
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.1, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      osc.start(t); osc.stop(t + 0.16);
    });
  }, [audioContext, isSoundEnabled]);

  const playDeath = useCallback(() => {
    if (!audioContext || !isSoundEnabled) return;
    const now = audioContext.currentTime;
    const freqs = [440, 370, 311, 262, 220];
    freqs.forEach((f, i) => {
      const osc = audioContext.createOscillator(); const g = audioContext.createGain();
      osc.connect(g); g.connect(audioContext.destination);
      osc.type = 'sawtooth'; osc.frequency.value = f;
      const t = now + i * 0.1;
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.12, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      osc.start(t); osc.stop(t + 0.2);
    });
  }, [audioContext, isSoundEnabled]);

  const playMove = useCallback(() => {
    if (!audioContext || !isSoundEnabled) return;
    const osc = audioContext.createOscillator(); const g = audioContext.createGain();
    osc.connect(g); g.connect(audioContext.destination);
    osc.type = 'sine'; osc.frequency.value = 200 + Math.random() * 40;
    const now = audioContext.currentTime;
    g.gain.setValueAtTime(0, now); g.gain.linearRampToValueAtTime(0.025, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
    osc.start(now); osc.stop(now + 0.05);
  }, [audioContext, isSoundEnabled]);

  const randomFood = (snake) => {
    const cols = COLS(), rows = ROWS();
    const snakeSet = new Set(snake.map(s => `${s.x},${s.y}`));
    let pos;
    do {
      pos = { x: Math.floor(Math.random() * cols), y: Math.floor(Math.random() * rows), hue: Math.random() * 360 };
    } while (snakeSet.has(`${pos.x},${pos.y}`));
    return pos;
  };

  const initState = () => {
    const cols = COLS(), rows = ROWS();
    const cx = Math.floor(cols / 2), cy = Math.floor(rows * 0.75);
    const snake = [];
    for (let i = 0; i < 6; i++) snake.push({ x: cx, y: cy + i });
    const food = [];
    for (let i = 0; i < FOOD_COUNT; i++) food.push(randomFood(snake));
    return {
      snake,
      dir: { x: 0, y: -1 },
      pendingDir: { x: 0, y: -1 },
      food,
      grow: 0,
      score: 0,
      speed: INITIAL_SPEED,
      particles: []
    };
  };

  const getDesiredDir = () => pendingDirRef.current;

  const draw = (ctx, state) => {
    const canvas = ctx.canvas;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Grid dots
    ctx.fillStyle = 'rgba(100, 220, 120, 0.06)';
    for (let x = 0; x < COLS(); x++)
      for (let y = 0; y < ROWS(); y++) {
        ctx.beginPath();
        ctx.arc(x * CELL + CELL / 2, y * CELL + CELL / 2, 1, 0, Math.PI * 2);
        ctx.fill();
      }

    // Food
    state.food.forEach(f => {
      const cx = f.x * CELL + CELL / 2, cy = f.y * CELL + CELL / 2;
      // Glow
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, CELL * 0.7);
      grad.addColorStop(0, `hsla(${f.hue}, 90%, 65%, 0.6)`);
      grad.addColorStop(1, `hsla(${f.hue}, 90%, 65%, 0)`);
      ctx.beginPath();
      ctx.arc(cx, cy, CELL * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = grad; ctx.fill();
      // Core
      ctx.beginPath();
      ctx.arc(cx, cy, CELL * 0.28, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${f.hue}, 90%, 70%, 1)`; ctx.fill();
      // Highlight
      ctx.beginPath();
      ctx.arc(cx - CELL * 0.1, cy - CELL * 0.1, CELL * 0.1, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.fill();
    });

    // Particles
    state.particles.forEach(p => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${p.hue}, 90%, 65%, ${p.alpha})`;
      ctx.fill();
    });

    // Snake body
    state.snake.forEach((seg, i) => {
      const isHead = i === 0;
      const len = state.snake.length;
      const t = i / Math.max(len - 1, 1);
      const cx = seg.x * CELL + CELL / 2, cy = seg.y * CELL + CELL / 2;
      const radius = isHead ? CELL * 0.48 : CELL * 0.38 * (1 - t * 0.3);

      // Glow for head
      if (isHead) {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, CELL);
        grad.addColorStop(0, 'rgba(80, 255, 120, 0.35)');
        grad.addColorStop(1, 'rgba(80, 255, 120, 0)');
        ctx.beginPath(); ctx.arc(cx, cy, CELL, 0, Math.PI * 2);
        ctx.fillStyle = grad; ctx.fill();
      }

      // Segment body gradient
      const segGrad = ctx.createRadialGradient(cx - radius * 0.3, cy - radius * 0.3, 0, cx, cy, radius);
      if (isHead) {
        segGrad.addColorStop(0, `rgba(140, 255, 160, 0.98)`);
        segGrad.addColorStop(1, `rgba(30, 180, 60, 0.95)`);
      } else {
        const g = Math.round(180 - t * 80), b = Math.round(60 - t * 30);
        segGrad.addColorStop(0, `rgba(80, ${g + 40}, ${b + 20}, 0.95)`);
        segGrad.addColorStop(1, `rgba(30, ${g}, ${b}, 0.92)`);
      }
      ctx.beginPath(); ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.fillStyle = segGrad; ctx.fill();

      // Scale pattern
      if (!isHead && i % 2 === 0) {
        ctx.beginPath(); ctx.arc(cx, cy, radius * 0.55, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 120, 40, 0.3)'; ctx.lineWidth = 0.8; ctx.stroke();
      }

      // Eyes on head
      if (isHead) {
        const d = state.dir;
        const ex = d.x !== 0 ? 0.15 : 0.25, ey = d.y !== 0 ? 0.15 : 0.25;
        const perp = { x: -d.y, y: d.x };
        [[1, -1], [-1, 1]].forEach(([sf, _]) => {
          const ex2 = cx + (d.x * 0.12 + perp.x * sf * 0.24) * CELL;
          const ey2 = cy + (d.y * 0.12 + perp.y * sf * 0.24) * CELL;
          ctx.beginPath(); ctx.arc(ex2, ey2, CELL * 0.14, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(0,0,0,0.85)'; ctx.fill();
          ctx.beginPath(); ctx.arc(ex2 + CELL * 0.04, ey2 - CELL * 0.04, CELL * 0.055, 0, Math.PI * 2);
          ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.fill();
        });
        // Tongue
        const tx = cx + d.x * CELL * 0.6, ty = cy + d.y * CELL * 0.6;
        ctx.beginPath(); ctx.moveTo(cx + d.x * CELL * 0.45, cy + d.y * CELL * 0.45);
        ctx.lineTo(tx - d.y * CELL * 0.12, ty + d.x * CELL * 0.12);
        ctx.moveTo(cx + d.x * CELL * 0.45, cy + d.y * CELL * 0.45);
        ctx.lineTo(tx + d.y * CELL * 0.12, ty - d.x * CELL * 0.12);
        ctx.strokeStyle = 'rgba(255,80,80,0.9)'; ctx.lineWidth = 1.5; ctx.stroke();
      }
    });
  };

  const update = (state, now) => {
    // Update particles
    const particles = state.particles
      .map(p => ({ ...p, x: p.x + p.vx, y: p.y + p.vy, r: p.r * 0.93, alpha: p.alpha * 0.92 }))
      .filter(p => p.alpha > 0.01);

    if (now - lastMoveRef.current < state.speed) return { ...state, particles };
    lastMoveRef.current = now;

    // Get new direction from keyboard
    const desired = getDesiredDir();
    let dir = state.dir;
    if (desired && !(desired.x === -state.dir.x && desired.y === -state.dir.y)) {
      if (desired.x !== state.dir.x || desired.y !== state.dir.y) dir = desired;
    }

    const head = state.snake[0];
    const cols = COLS(), rows = ROWS();
    let nx = (head.x + dir.x + cols) % cols;
    let ny = (head.y + dir.y + rows) % rows;

    // Collision with self
    const selfHit = state.snake.slice(1).some(s => s.x === nx && s.y === ny);
    if (selfHit) return null; // Signal death

    const newHead = { x: nx, y: ny };
    let newSnake = [newHead, ...state.snake];
    let grow = state.grow;
    let newFood = state.food;
    let newScore = state.score;
    let newSpeed = state.speed;
    let eatParticles = [];

    // Check food
    const fi = newFood.findIndex(f => f.x === nx && f.y === ny);
    if (fi !== -1) {
      grow += 4;
      newScore += 1;
      const eaten = newFood[fi];
      const px = nx * CELL + CELL / 2, py = ny * CELL + CELL / 2;
      for (let j = 0; j < 8; j++) {
        const angle = (j / 8) * Math.PI * 2;
        eatParticles.push({
          x: px, y: py,
          vx: Math.cos(angle) * (1.5 + Math.random()),
          vy: Math.sin(angle) * (1.5 + Math.random()),
          r: 3 + Math.random() * 3, alpha: 1,
          hue: eaten.hue
        });
      }
      newFood = [...newFood.filter((_, i) => i !== fi), randomFood(newSnake)];
      newSpeed = Math.max(MIN_SPEED, INITIAL_SPEED - newScore * 3);
      playEat();
    } else {
      if (grow > 0) {
        grow--;
      } else {
        newSnake = newSnake.slice(0, -1);
      }
    }

    // Pulse move sound every 4 steps
    if (newScore % 2 === 0 && Math.random() > 0.8) playMove();

    return { ...state, snake: newSnake, dir, grow, food: newFood, score: newScore, speed: newSpeed, particles: [...particles, ...eatParticles] };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    stateRef.current = initState();
    setScore(0);
    setIsDead(false);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    const loop = (now) => {
      if (!stateRef.current) return;
      const s = stateRef.current;
      const result = update(s, now);
      if (result === null) {
        // Death
        playDeath();
        setIsDead(true);
        scoreRef.current = s.score;
        setScore(s.score);
        draw(ctx, s);
        // Submit score
        submitSnakeScore(s.score)
          .then(res => {
            if (res.is_server_record) setRecordMsg('server');
            else if (res.is_personal_record) setRecordMsg('personal');
            setBestScore(res.best_score);
          })
          .catch(() => {});
        return;
      }
      stateRef.current = result;
      if (result.score !== s.score) {
        scoreRef.current = result.score;
        setScore(result.score);
      }
      draw(ctx, result);
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);

    const handleKey = (e) => {
      switch (e.key) {
        case 'ArrowUp':    case 'w': case 'W': case 'ц': case 'Ц': pendingDirRef.current = { x: 0, y: -1 }; e.preventDefault(); break;
        case 'ArrowDown':  case 's': case 'S': case 'ы': case 'Ы': pendingDirRef.current = { x: 0, y:  1 }; e.preventDefault(); break;
        case 'ArrowLeft':  case 'a': case 'A': case 'ф': case 'Ф': pendingDirRef.current = { x: -1, y: 0 }; e.preventDefault(); break;
        case 'ArrowRight': case 'd': case 'D': case 'в': case 'В': pendingDirRef.current = { x:  1, y: 0 }; e.preventDefault(); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKey);
    };
  }, []);

  const restart = () => {
    stateRef.current = initState();
    setIsDead(false);
    setScore(0);
    setShowHint(false);
    setRecordMsg(null);
    setBestScore(null);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const loop = (now) => {
      if (!stateRef.current) return;
      const s = stateRef.current;
      const result = update(s, now);
      if (result === null) {
        playDeath();
        setIsDead(true);
        scoreRef.current = s.score;
        setScore(s.score);
        submitSnakeScore(s.score)
          .then(res => {
            if (res.is_server_record) setRecordMsg('server');
            else if (res.is_personal_record) setRecordMsg('personal');
            setBestScore(res.best_score);
          })
          .catch(() => {});
        draw(ctx, s);
        return;
      }
      stateRef.current = result;
      if (result.score !== s.score) {
        scoreRef.current = result.score;
        setScore(result.score);
      }
      draw(ctx, result);
      animRef.current = requestAnimationFrame(loop);
    };
    cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(loop);
  };

  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 7000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape' && onExit) onExit();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExit]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99990, overflow: 'hidden', pointerEvents: 'none' }}>
      <style>{`
        @keyframes snakeHintIn { from{opacity:0;transform:translateX(-50%) translateY(14px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
        @keyframes snakePulse { 0%,100%{transform:scale(1)} 50%{transform:scale(1.03)} }
        @keyframes deadIn { from{opacity:0;transform:translateX(-50%) scale(0.85)} to{opacity:1;transform:translateX(-50%) scale(1)} }
      `}</style>

      <canvas ref={canvasRef}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
          background: 'linear-gradient(180deg, rgba(0,10,5,0.94) 0%, rgba(0,20,10,0.94) 100%)'
        }} />

      {/* Close button */}
      {onExit && (
        <button
          onClick={onExit}
          title="Закрыть змейку (Esc)"
          style={{
            position: 'fixed', top: '18px', right: '18px',
            width: '36px', height: '36px', borderRadius: '50%',
            background: 'rgba(0,30,15,0.85)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(50,200,80,0.35)',
            color: 'rgba(150,255,150,0.9)', fontSize: '16px',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: 'all', transition: 'all 0.2s',
            lineHeight: 1,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,40,40,0.85)'; e.currentTarget.style.borderColor = 'rgba(255,100,80,0.5)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,30,15,0.85)'; e.currentTarget.style.borderColor = 'rgba(50,200,80,0.35)'; e.currentTarget.style.color = 'rgba(150,255,150,0.9)'; }}
        >✕</button>
      )}

      {/* Score */}
      <div style={{
        position: 'fixed', top: '22px', left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(0,30,15,0.8)', backdropFilter: 'blur(12px)',
        border: '1px solid rgba(50,200,80,0.3)', borderRadius: '12px',
        padding: '6px 20px', color: 'rgba(100,255,130,0.95)', fontSize: '16px',
        fontWeight: '600', letterSpacing: '2px', pointerEvents: 'none',
        animation: 'snakePulse 2s ease infinite'
      }}>
        🐍 {score}
      </div>

      {showHint && !isDead && (
        <div style={{
          position: 'fixed', bottom: '160px', left: '50%',
          background: 'rgba(0,25,12,0.92)', backdropFilter: 'blur(14px)', borderRadius: '14px',
          padding: '12px 20px', border: '1px solid rgba(50,200,80,0.3)',
          color: 'rgba(120,255,140,0.95)', fontSize: '13px',
          animation: 'snakeHintIn 0.4s ease', pointerEvents: 'none', whiteSpace: 'nowrap'
        }}>
          🐍 Управляй змейкой: стрелки или W A S D · Собирай цветные точки
        </div>
      )}

      {isDead && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'rgba(10,5,0,0.93)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,100,50,0.4)', borderRadius: '20px',
          padding: '28px 40px', textAlign: 'center', pointerEvents: 'all',
          animation: 'deadIn 0.4s ease', minWidth: '240px'
        }}>
          <div style={{ fontSize: '38px', marginBottom: '8px' }}>💀</div>
          <div style={{ color: 'rgba(255,150,100,0.95)', fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
            Игра окончена
          </div>
          <div style={{ color: 'rgba(200,200,200,0.7)', fontSize: '14px', marginBottom: '18px' }}>
            Счёт: <span style={{ color: 'rgba(100,255,130,0.95)', fontWeight: '600' }}>{score}</span>
          </div>
          {recordMsg === 'server' && (
            <div style={{ color: '#ffd700', fontSize: '15px', fontWeight: '700', marginBottom: '10px', textShadow: '0 0 12px #ffd700' }}>
              🏆 Рекорд сервера! Ты лучший!
            </div>
          )}
          {recordMsg === 'personal' && (
            <div style={{ color: 'rgba(100,255,130,0.95)', fontSize: '15px', fontWeight: '700', marginBottom: '10px', textShadow: '0 0 10px rgba(100,255,130,0.6)' }}>
              ⭐ Личный рекорд! Ты побил свой результат!
            </div>
          )}
          {!recordMsg && bestScore !== null && (
            <div style={{ color: 'rgba(180,180,180,0.7)', fontSize: '13px', marginBottom: '10px' }}>
              Ваш рекорд: <span style={{ color: 'rgba(100,255,130,0.85)', fontWeight: '600' }}>{bestScore}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={restart} style={{
              background: 'linear-gradient(135deg, rgba(30,160,60,0.8), rgba(20,100,40,0.8))',
              border: '1px solid rgba(50,220,80,0.4)', borderRadius: '10px',
              color: 'rgba(150,255,160,0.95)', fontSize: '14px', fontWeight: '600',
              padding: '8px 22px', cursor: 'pointer', transition: 'all 0.2s',
            }}>Ещё раз</button>
            {onExit && (
              <button onClick={onExit} style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px',
                color: 'rgba(200,200,200,0.8)', fontSize: '14px', fontWeight: '600',
                padding: '8px 22px', cursor: 'pointer', transition: 'all 0.2s',
              }}>Выйти</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SnakeMode;
